/* TeraMart Vibe — motion system
   - IntersectionObserver for scroll-triggered reveals
   - Sticky header scroll-shadow toggle
   - Auto-wires data-reveal onto common section elements
   - AJAX loading bar
   - Confetti burst on add-to-cart
*/
(function () {
  'use strict';

  const doc = document;
  const root = doc.documentElement;

  // --- Auto-tag sections for reveal --------------------------------
  function autoTag() {
    const selectors = [
      '.product-row-section',
      '.category-tiles-section',
      '.brand-carousels-section',
      '.hero-showcase-section',
      '.image-banner-section',
      '.tech-drops-section',
      '.refurb-hub-section',
      '.featured-collection-section',
      '.promo-cards-section',
      '.rating-strip-section',
      '.certified-strip-section',
      '.promise-strip-section',
      '.brand-logos-section',
      '.multicolumn-section',
      '.newsletter-section',
      '.image-with-text-section',
      '.finance-banner-section',
      '.flash-deal-section',
      '.top-offers-section',
      '.products-spotlight-section',
      '.mega-categories-section'
    ];
    doc.querySelectorAll(selectors.join(',')).forEach(el => {
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'fade-up');
    });

    // Stagger grids
    doc.querySelectorAll(
      '.product-row__grid, .category-tiles, .bc-row__track, .collection-grid, .brand-logos__grid'
    ).forEach(el => {
      if (!el.hasAttribute('data-reveal-stagger')) el.setAttribute('data-reveal-stagger', '');
    });
  }

  // --- Reveal observer --------------------------------------------
  function initReveal() {
    const elements = doc.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!('IntersectionObserver' in window) || elements.length === 0) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.08 });
    elements.forEach(el => io.observe(el));
  }

  // --- Sticky header scroll shadow ---------------------------------
  function initHeaderScroll() {
    const header = doc.querySelector('.site-header, header.header, .header');
    if (!header) return;
    let ticking = false;
    function update() {
      if (window.scrollY > 20) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  // --- Announcement bar: duplicate children for marquee loop -------
  function initMarquee() {
    doc.querySelectorAll('.announcement-bar__track').forEach(track => {
      if (track.dataset.vibeMarqueeReady) return;
      track.dataset.vibeMarqueeReady = '1';
      const clone = track.innerHTML;
      track.innerHTML = clone + clone;
    });
  }

  // --- Smooth anchor scroll ---------------------------------------
  function initSmoothScroll() {
    doc.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (id === '#' || id.length < 2) return;
      const target = doc.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // --- Top progress bar for AJAX navigations -----------------------
  let _bar = null;
  function showLoader() {
    if (!_bar) {
      _bar = doc.createElement('div');
      _bar.className = 'vibe-loader';
      _bar.style.width = '0';
      doc.body.appendChild(_bar);
    }
    _bar.style.width = '30%';
    requestAnimationFrame(() => _bar.style.width = '75%');
  }
  function hideLoader() {
    if (!_bar) return;
    _bar.style.width = '100%';
    setTimeout(() => { if (_bar) _bar.style.width = '0'; }, 300);
  }
  window.VibeLoader = { show: showLoader, hide: hideLoader };

  // --- Button ripple / press pulse ---------------------------------
  function initButtonPress() {
    doc.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn--accent, .btn--primary, button.btn--accent, button.btn--primary');
      if (!btn) return;
      btn.style.transform = 'scale(0.96)';
      setTimeout(() => btn.style.transform = '', 140);
    });
  }

  // --- Confetti burst on add-to-cart (CSS-only tiny shapes) --------
  function burst(x, y) {
    const colors = ['#FF5A70', '#FF8FA5', '#FFEBEF', '#1A1A20', '#FF3860'];
    const N = 14;
    const host = doc.createElement('div');
    host.style.cssText = `
      position:fixed;left:${x}px;top:${y}px;pointer-events:none;z-index:9998;
    `;
    for (let i = 0; i < N; i++) {
      const s = doc.createElement('span');
      const angle = (Math.PI * 2) * (i / N) + Math.random()*0.4;
      const dist = 60 + Math.random()*60;
      const dx = Math.cos(angle)*dist;
      const dy = Math.sin(angle)*dist;
      s.style.cssText = `
        position:absolute;left:0;top:0;width:8px;height:8px;border-radius:2px;
        background:${colors[i % colors.length]};
        transform:translate(-50%,-50%);
        transition:transform .8s cubic-bezier(.2,.7,.2,1), opacity .8s ease-out;
        opacity:1;
      `;
      host.appendChild(s);
      requestAnimationFrame(() => {
        s.style.transform = `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) rotate(${Math.random()*360}deg)`;
        s.style.opacity = '0';
      });
    }
    doc.body.appendChild(host);
    setTimeout(() => host.remove(), 900);
  }
  function initConfetti() {
    doc.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart], form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      burst(r.left + r.width/2, r.top + r.height/2);
    });
  }
  window.VibeBurst = burst;

  // --- Refurb-hub slideshow (and any .refurb-slides element) -----
  function initRefurbSlides() {
    doc.querySelectorAll('[data-refurb-slides]').forEach(root => {
      const track  = root.querySelector('[data-refurb-slides-track]');
      const slides = root.querySelectorAll('[data-refurb-slide]');
      const dots   = root.querySelectorAll('[data-refurb-slide-dot]');
      const prev   = root.querySelector('[data-refurb-slides-prev]');
      const next   = root.querySelector('[data-refurb-slides-next]');
      if (!track || slides.length < 2) return;

      let i = 0;
      let timer = null;
      const autoplayMs = (parseInt(root.getAttribute('data-autoplay-seconds') || '6', 10) || 6) * 1000;

      function go(n) {
        i = (n + slides.length) % slides.length;
        track.style.transform = `translateX(-${i * 100}%)`;
        dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      }
      function schedule() {
        clearInterval(timer);
        if (autoplayMs > 0) timer = setInterval(() => go(i + 1), autoplayMs);
      }

      prev && prev.addEventListener('click', () => { go(i - 1); schedule(); });
      next && next.addEventListener('click', () => { go(i + 1); schedule(); });
      dots.forEach((d, idx) => d.addEventListener('click', () => { go(idx); schedule(); }));

      root.addEventListener('mouseenter', () => clearInterval(timer));
      root.addEventListener('mouseleave', schedule);

      // Touch swipe
      let startX = null;
      track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend',   (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1));
        startX = null;
        schedule();
      });

      go(0);
      schedule();
    });
  }

  // --- Init ---------------------------------------------------------
  function init() {
    autoTag();
    initReveal();
    initHeaderScroll();
    initMarquee();
    initSmoothScroll();
    initButtonPress();
    initConfetti();
    initRefurbSlides();
    root.classList.add('vibe-ready');
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
